import { useEffect, useState } from 'react';

import useLoading from '@proton/hooks/useLoading';
import { PAYMENT_METHOD_TYPES } from '@proton/payments/core/constants';
import type { PaymentVerificatorV5 } from '@proton/payments/core/createPaymentToken';
import type {
    AmountAndCurrency,
    ChargeableV5PaymentParameters,
    ChargebeeIframeEvents,
    ChargebeeIframeHandles,
    PaymentMethodType,
    PaymentStatus,
    PlainPaymentMethodType,
} from '@proton/payments/core/interface';
import {
    ChargebeeCardPaymentProcessor,
    type ChargebeeCardPaymentProcessorState,
} from '@proton/payments/core/payment-processors/chargebeeCardPayment';
import type { PaymentProcessorHook } from '@proton/payments/core/payment-processors/interface';
import type { Api } from '@proton/shared/lib/interfaces/Api';
import noop from '@proton/utils/noop';

import { usePaymentProcessor } from './usePaymentProcessor';

export interface Props {
    amountAndCurrency: AmountAndCurrency;
    onChargeable: (data: ChargeableV5PaymentParameters) => Promise<unknown>;
    verifyOnly?: boolean;
    paymentStatus?: PaymentStatus;
    onDeclined: ({
        selectedMethodType,
        selectedMethodValue,
    }: {
        selectedMethodType: PlainPaymentMethodType;
        selectedMethodValue: PaymentMethodType;
    }) => void;
    onValidationFailed: ({
        selectedMethodType,
        selectedMethodValue,
    }: {
        selectedMethodType: PlainPaymentMethodType;
        selectedMethodValue: PaymentMethodType;
    }) => void;
}

export interface Dependencies {
    api: Api;
    verifyPayment: PaymentVerificatorV5;
    handles: ChargebeeIframeHandles;
    events: ChargebeeIframeEvents;
}

type Overrides = {
    verifyPaymentToken: () => Promise<ChargeableV5PaymentParameters>;
    paymentProcessor?: ChargebeeCardPaymentProcessor;
    processPaymentToken: () => Promise<ChargeableV5PaymentParameters | void>;
};

export type ChargebeeCardProcessorHook = Omit<PaymentProcessorHook, keyof Overrides> & {
    countryCode: string;
    setCountryCode: (countryCode: string) => void;
    postalCode: string;
    setPostalCode: (postalCode: string) => void;
    errors: Record<string, string>;
    submitted: boolean;
} & Overrides;

export const useChargebeeCard = (
    { amountAndCurrency, onChargeable, verifyOnly, paymentStatus, onDeclined, onValidationFailed }: Props,
    { api, verifyPayment, handles, events }: Dependencies
): ChargebeeCardProcessorHook => {
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitted, setSubmitted] = useState(false);

    const [countryCode, setCountryCode] = useState('');
    const [postalCode, setPostalCode] = useState('');

    const [fetchingToken, withFetchingToken] = useLoading();
    const [verifyingToken, withVerifyingToken] = useLoading();
    const [userInitiatedProcessing, withUserInitiatiatedProcessing] = useLoading();
    const processingToken = fetchingToken || verifyingToken;

    const paymentProcessor = usePaymentProcessor(
        () =>
            new ChargebeeCardPaymentProcessor(
                verifyPayment,
                api,
                amountAndCurrency,
                handles,
                events,
                !!verifyOnly,
                paymentStatus,
                onChargeable,
                () => {
                    onDeclined({
                        selectedMethodType: PAYMENT_METHOD_TYPES.CHARGEBEE_CARD,
                        selectedMethodValue: PAYMENT_METHOD_TYPES.CHARGEBEE_CARD,
                    });
                },
                () => {
                    onValidationFailed({
                        selectedMethodType: PAYMENT_METHOD_TYPES.CHARGEBEE_CARD,
                        selectedMethodValue: PAYMENT_METHOD_TYPES.CHARGEBEE_CARD,
                    });
                }
            )
    );

    useEffect(() => {
        const setters: Record<keyof ChargebeeCardPaymentProcessorState, (...args: any[]) => any> = {
            countryCode: setCountryCode,
            postalCode: setPostalCode,
            submitted: setSubmitted,
        };

        paymentProcessor.onStateUpdated(
            (updatedProperties) => {
                for (const [key, value] of Object.entries(updatedProperties)) {
                    const setter = setters[key as keyof ChargebeeCardPaymentProcessorState];
                    setter?.(value);
                }

                setErrors(paymentProcessor.getErrors());
            },
            {
                initial: true,
            }
        );

        return () => paymentProcessor.destroy();
    }, []);

    useEffect(() => {
        paymentProcessor.amountAndCurrency = amountAndCurrency;
        paymentProcessor.reset();
    }, [amountAndCurrency]);

    useEffect(() => {
        paymentProcessor.onTokenIsChargeable = onChargeable;
    }, [onChargeable]);

    useEffect(() => {
        paymentProcessor.verifyOnly = !!verifyOnly;
    }, [verifyOnly]);

    const reset = () => paymentProcessor.reset();

    const fetchPaymentToken = async () => {
        const promise = paymentProcessor.fetchPaymentToken();
        withFetchingToken(promise).catch(noop);
        return promise;
    };
    const verifyPaymentToken = () => {
        const tokenPromise = paymentProcessor.verifyPaymentToken();
        withVerifyingToken(tokenPromise).catch(noop);
        return tokenPromise;
    };

    const processPaymentToken = () => {
        return withUserInitiatiatedProcessing(async () => {
            if (!paymentProcessor.fetchedPaymentToken) {
                await fetchPaymentToken();
            }

            try {
                return await verifyPaymentToken();
            } catch (error) {
                reset();
                throw error;
            }
        });
    };

    return {
        fetchPaymentToken,
        fetchingToken,
        verifyPaymentToken,
        verifyingToken,
        countryCode,
        postalCode,
        setPostalCode: (postalCode: string) => paymentProcessor.setPostalCode(postalCode),
        setCountryCode: (countryCode: string) => paymentProcessor.setCountryCode(countryCode),
        errors: submitted ? errors : {},
        submitted,
        paymentProcessor,
        processPaymentToken,
        processingToken,
        reset,
        userInitiatedProcessing,
        meta: {
            type: 'chargebee-card',
        },
    };
};
